import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Platform } from 'react-native';
import { AesUtil } from '../js/AesUtil';
import { DOOR_KEY_TYPES, LAUNCHED } from '../config/Constant';
import { setDoorKeyType, setOpenChat, setServiceStatus } from '../Redux/Reducers/appConfigReducer';
import { initializeDoorKey } from '../Apps/doorKey/initializeDoorkey';
import { initializeChat, handleOpenChat } from '../Apps/chat/initializeChat';
import { sendAnalytics } from '../Redux/Actions/analyticsAction';
import { clientConfigPath } from '../config/Constant';

/**
 * Custom hook to manage various app initializations
 * Includes: door keys, chat, FCM tokens, AES encryption
 */
export const useInitializations = () => {
    const dispatch = useDispatch();
    const renderAppTimeout = useRef(null);

    // Memoize AesUtil instance to avoid recreation on every render
    const aesUtil = useMemo(() => new AesUtil(128, 1000), []);

    const {
        webAppWidgetsInfo,
        fcmToken,
        isLoggedIn,
        isOnline,
        chatInterface,
        openChat,
        renderApp,
        appSessionToken
    } = useSelector(
        state => ({
            webAppWidgetsInfo: state.deviceConfigSlice.webAppWidgetsInfo,
            fcmToken: state.appConfigSlice.fcmToken,
            isLoggedIn: state.appConfigSlice.isLoggedIn,
            isOnline: state.appConfigSlice.isOnline,
            chatInterface: state.appPreservedConfigSlice.chatInterface,
            openChat: state.appConfigSlice.openChat,
            renderApp: state.appConfigSlice.renderApp,
            appSessionToken: state.appConfigSlice.appSessionToken,
        }),
        shallowEqual
    );

    /**
     * Initialize door key interface
     */
    const updateAndInitializeDoorkeyInterface = () => {
        const digitalKeyInterface = webAppWidgetsInfo.appConfigs?.digitalKeyInterface;

        if (digitalKeyInterface) {
            const doorKeyType = Object.keys(DOOR_KEY_TYPES).find((key) =>
                DOOR_KEY_TYPES[key].includes(digitalKeyInterface)
            );

            if (doorKeyType) {
                dispatch(setDoorKeyType(doorKeyType));
                dispatch(initializeDoorKey(doorKeyType));
            }
        }
    };

    // Initialize door key when widgets are loaded
    useEffect(() => {
        if (Object.keys(webAppWidgetsInfo).length > 0) {
            updateAndInitializeDoorkeyInterface();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webAppWidgetsInfo, fcmToken, isLoggedIn]);



    // Open chat when requested
    useEffect(() => {
        if (chatInterface?.interfaceKey && openChat) {
            dispatch(setOpenChat(false));
            dispatch(handleOpenChat());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatInterface?.interfaceKey, openChat]);

    // Initialize chat when online and interface key is available
    useEffect(() => {
        if (isOnline && chatInterface.interfaceKey) {
            dispatch(initializeChat());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline, chatInterface.interfaceKey]);

    return null;
};
